import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useScopedStorageKey } from "@/workspaces"

import { useQueryParams } from "@/hooks/useQueryParams"

import type { ChartClickData } from "../widgets/types/chart"

const DRILL_DOWN_WIDGET_QUERY_PARAM = "drillDownWidget"
const METRIC_ID_QUERY_PARAM = "metricId"
const DRILL_DOWN_STATE_QUERY_PARAM = "drillDownState"
const DRILL_DOWN_STATE_STORAGE_KEY_PREFIX = "dashboard-unified-metric-drilldown"

export interface UnifiedMetricDrillDownState {
  clickData: ChartClickData
  widgetId: string
  metricId: string | null
  stateId: string
  parentStateId: string | null
}

let drillDownStateSequence = 0

const getLegacyDrillDownStateKey = (
  widgetId: string,
  metricId: string | null,
) => `${widgetId}:${metricId ?? ""}`

const getDrillDownStateKey = (stateId: string) => `state:${stateId}`

const getUrlDrillDownStateKey = (
  widgetId: string,
  metricId: string | null,
  stateId: string | null,
) =>
  stateId
    ? getDrillDownStateKey(stateId)
    : getLegacyDrillDownStateKey(widgetId, metricId)

const createDrillDownStateId = (
  clickData: ChartClickData,
  widgetId: string,
  metricId: string | null,
) =>
  [
    widgetId,
    metricId ?? "",
    clickData.drillDownScope ?? "point",
    clickData.timestamp,
    clickData.endDate ?? "",
    clickData.bucketTime,
    ++drillDownStateSequence,
  ].join(":")

const getStoredDrillDownStateKey = (prefix: string, stateKey: string) =>
  `${prefix}:${stateKey}`

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value)

const isValidDrillDownClickData = (value: unknown): value is ChartClickData => {
  if (!isRecord(value)) {
    return false
  }

  return (
    typeof value.timestamp === "number" &&
    typeof value.bucketTime === "string" &&
    typeof value.metricId === "string" &&
    typeof value.dataSource === "string" &&
    typeof value.aggregation === "string" &&
    typeof value.metricOutputType === "string" &&
    (value.drillDownScope === undefined ||
      value.drillDownScope === "chart" ||
      value.drillDownScope === "point") &&
    (value.bucketInterval === undefined ||
      typeof value.bucketInterval === "string") &&
    (typeof value.aggregatedValue === "number" ||
      typeof value.aggregatedValue === "string") &&
    typeof value.displayLabel === "string"
  )
}

const readStoredDrillDownState = (
  prefix: string | undefined,
  stateKey: string,
): UnifiedMetricDrillDownState | null => {
  if (typeof window === "undefined" || !prefix) {
    return null
  }

  try {
    const rawState = window.sessionStorage.getItem(
      getStoredDrillDownStateKey(prefix, stateKey),
    )
    if (!rawState) {
      return null
    }

    const parsedState = JSON.parse(rawState) as unknown

    if (!isRecord(parsedState)) {
      return null
    }

    const metricId =
      typeof parsedState.metricId === "string" ? parsedState.metricId : null
    const parentStateId =
      typeof parsedState.parentStateId === "string"
        ? parsedState.parentStateId
        : null
    const stateId =
      typeof parsedState.stateId === "string" ? parsedState.stateId : stateKey
    const legacyParsedStateKey = getLegacyDrillDownStateKey(
      typeof parsedState.widgetId === "string" ? parsedState.widgetId : "",
      metricId,
    )
    const parsedStateKey =
      typeof parsedState.stateId === "string"
        ? getDrillDownStateKey(parsedState.stateId)
        : legacyParsedStateKey

    if (
      typeof parsedState.widgetId !== "string" ||
      (parsedState.metricId != null &&
        typeof parsedState.metricId !== "string") ||
      !isValidDrillDownClickData(parsedState.clickData) ||
      (parsedStateKey !== stateKey && legacyParsedStateKey !== stateKey)
    ) {
      return null
    }

    return {
      clickData: parsedState.clickData,
      widgetId: parsedState.widgetId,
      metricId,
      stateId,
      parentStateId,
    }
  } catch {
    return null
  }
}

const writeStoredDrillDownState = (
  prefix: string | undefined,
  state: UnifiedMetricDrillDownState,
) => {
  if (typeof window === "undefined" || !prefix) {
    return
  }

  try {
    window.sessionStorage.setItem(
      getStoredDrillDownStateKey(prefix, getDrillDownStateKey(state.stateId)),
      JSON.stringify(state),
    )
    window.sessionStorage.setItem(
      getStoredDrillDownStateKey(
        prefix,
        getLegacyDrillDownStateKey(state.widgetId, state.metricId),
      ),
      JSON.stringify(state),
    )
  } catch {
    // URL state still works when session storage is unavailable.
  }
}

export const useUnifiedMetricDrillDownNavigation = () => {
  // Scoped by org + workspace so a workspace switch can't resurrect another
  // workspace's drill-down click data via sessionStorage.
  const drillDownStorageKeyPrefix = useScopedStorageKey(
    DRILL_DOWN_STATE_STORAGE_KEY_PREFIX,
  )
  const queryParams = useQueryParams()
  const drillDownWidgetInUrl = queryParams.get(DRILL_DOWN_WIDGET_QUERY_PARAM)
  const metricIdInUrl = queryParams.get(METRIC_ID_QUERY_PARAM)
  const drillDownStateInUrl = queryParams.get(DRILL_DOWN_STATE_QUERY_PARAM)

  const [unifiedMetricDrillDown, setUnifiedMetricDrillDown] =
    useState<UnifiedMetricDrillDownState | null>(null)

  const drillDownStateCacheRef = useRef<
    Map<string, UnifiedMetricDrillDownState>
  >(new Map())

  // Rescope, not just re-key: on an org/workspace switch that keeps this
  // component mounted (e.g. two orgs both without a resolved workspace share
  // the same "global" route-tree key), the sessionStorage key changes but
  // this in-memory cache and the currently-open drill-down do not — without
  // this, a widget/metric id that collides across tenants would serve the
  // previous org's cached click data before the new scope is even read.
  const initializedForStoragePrefixRef = useRef(drillDownStorageKeyPrefix)
  useEffect(() => {
    if (initializedForStoragePrefixRef.current === drillDownStorageKeyPrefix) {
      return
    }
    initializedForStoragePrefixRef.current = drillDownStorageKeyPrefix
    drillDownStateCacheRef.current.clear()
    setUnifiedMetricDrillDown(null)
  }, [drillDownStorageKeyPrefix])

  const desiredUrlDrillDownKeyRef = useRef<string | null>(
    drillDownWidgetInUrl
      ? getUrlDrillDownStateKey(
          drillDownWidgetInUrl,
          metricIdInUrl,
          drillDownStateInUrl,
        )
      : null,
  )
  const lastSeenUrlDrillDownKeyRef = useRef<string | null>(
    desiredUrlDrillDownKeyRef.current,
  )
  const suppressedUrlDrillDownKeyRef = useRef<string | null>(null)
  const suppressUrlDrillDownRestoreRef = useRef(false)

  const currentUrlDrillDownKey = useMemo(() => {
    if (!drillDownWidgetInUrl) {
      return null
    }

    return getUrlDrillDownStateKey(
      drillDownWidgetInUrl,
      metricIdInUrl,
      drillDownStateInUrl,
    )
  }, [drillDownStateInUrl, drillDownWidgetInUrl, metricIdInUrl])

  const currentStateDrillDownKey = useMemo(() => {
    if (!unifiedMetricDrillDown) {
      return null
    }

    return getDrillDownStateKey(unifiedMetricDrillDown.stateId)
  }, [unifiedMetricDrillDown])

  const getCachedDrillDownState = useCallback(
    (stateKey: string) => {
      const cachedDrillDownState = drillDownStateCacheRef.current.get(stateKey)
      if (cachedDrillDownState) {
        return cachedDrillDownState
      }

      const storedDrillDownState = readStoredDrillDownState(
        drillDownStorageKeyPrefix,
        stateKey,
      )
      if (storedDrillDownState) {
        drillDownStateCacheRef.current.set(stateKey, storedDrillDownState)
        drillDownStateCacheRef.current.set(
          getLegacyDrillDownStateKey(
            storedDrillDownState.widgetId,
            storedDrillDownState.metricId,
          ),
          storedDrillDownState,
        )
      }

      return storedDrillDownState
    },
    [drillDownStorageKeyPrefix],
  )

  const openDrillDownState = useCallback(
    (
      state: UnifiedMetricDrillDownState,
      options?: { history?: "push" | "replace" },
    ) => {
      const stateKey = getDrillDownStateKey(state.stateId)
      drillDownStateCacheRef.current.set(stateKey, state)
      drillDownStateCacheRef.current.set(
        getLegacyDrillDownStateKey(state.widgetId, state.metricId),
        state,
      )
      writeStoredDrillDownState(drillDownStorageKeyPrefix, state)
      setUnifiedMetricDrillDown(state)
      desiredUrlDrillDownKeyRef.current = stateKey

      if (currentUrlDrillDownKey === stateKey) {
        return
      }

      queryParams.setMultiple(
        {
          [DRILL_DOWN_WIDGET_QUERY_PARAM]: state.widgetId,
          [METRIC_ID_QUERY_PARAM]: state.metricId,
          [DRILL_DOWN_STATE_QUERY_PARAM]: state.stateId,
        },
        options,
      )
    },
    [currentUrlDrillDownKey, drillDownStorageKeyPrefix, queryParams],
  )

  const openUnifiedMetricDrillDown = useCallback(
    (clickData: ChartClickData, widgetId: string, metricId?: string) => {
      const resolvedMetricId = metricId ?? null
      const stateId = createDrillDownStateId(
        clickData,
        widgetId,
        resolvedMetricId,
      )
      const shouldNestUnderCurrentDrillDown =
        (clickData.drillDownScope ?? "point") === "point" &&
        unifiedMetricDrillDown?.clickData.drillDownScope === "chart"
      const nextDrillDownState = {
        clickData,
        widgetId,
        metricId: resolvedMetricId,
        stateId,
        parentStateId: shouldNestUnderCurrentDrillDown
          ? unifiedMetricDrillDown.stateId
          : null,
      }

      openDrillDownState(nextDrillDownState)
    },
    [openDrillDownState, unifiedMetricDrillDown],
  )

  const closeUnifiedMetricDrillDown = useCallback(() => {
    if (currentStateDrillDownKey) {
      suppressedUrlDrillDownKeyRef.current = currentStateDrillDownKey
      suppressUrlDrillDownRestoreRef.current = true
    }

    desiredUrlDrillDownKeyRef.current = null
    setUnifiedMetricDrillDown(null)

    if (!drillDownWidgetInUrl && !metricIdInUrl && !drillDownStateInUrl) {
      return
    }

    queryParams.setMultiple(
      {
        [DRILL_DOWN_WIDGET_QUERY_PARAM]: null,
        [METRIC_ID_QUERY_PARAM]: null,
        [DRILL_DOWN_STATE_QUERY_PARAM]: null,
      },
      { history: "replace" },
    )
  }, [
    currentStateDrillDownKey,
    drillDownStateInUrl,
    drillDownWidgetInUrl,
    metricIdInUrl,
    queryParams,
  ])

  useEffect(() => {
    const lastSeenUrlDrillDownKey = lastSeenUrlDrillDownKeyRef.current
    const hasUrlChanged = currentUrlDrillDownKey !== lastSeenUrlDrillDownKey

    if (hasUrlChanged) {
      lastSeenUrlDrillDownKeyRef.current = currentUrlDrillDownKey
    }

    const desiredUrlDrillDownKey = desiredUrlDrillDownKeyRef.current
    if (
      currentUrlDrillDownKey &&
      suppressUrlDrillDownRestoreRef.current &&
      !unifiedMetricDrillDown &&
      desiredUrlDrillDownKey === null
    ) {
      queryParams.setMultiple(
        {
          [DRILL_DOWN_WIDGET_QUERY_PARAM]: null,
          [METRIC_ID_QUERY_PARAM]: null,
          [DRILL_DOWN_STATE_QUERY_PARAM]: null,
        },
        { history: "replace" },
      )
      return
    }

    if (
      currentUrlDrillDownKey &&
      !suppressUrlDrillDownRestoreRef.current &&
      currentUrlDrillDownKey === suppressedUrlDrillDownKeyRef.current &&
      !unifiedMetricDrillDown &&
      desiredUrlDrillDownKey === null
    ) {
      suppressedUrlDrillDownKeyRef.current = null
      queryParams.setMultiple(
        {
          [DRILL_DOWN_WIDGET_QUERY_PARAM]: null,
          [METRIC_ID_QUERY_PARAM]: null,
          [DRILL_DOWN_STATE_QUERY_PARAM]: null,
        },
        { history: "replace" },
      )
      return
    }

    if (
      hasUrlChanged &&
      currentUrlDrillDownKey &&
      desiredUrlDrillDownKey &&
      currentUrlDrillDownKey !== desiredUrlDrillDownKey &&
      lastSeenUrlDrillDownKey === null &&
      unifiedMetricDrillDown
    ) {
      return
    }

    if (hasUrlChanged && !currentUrlDrillDownKey) {
      suppressedUrlDrillDownKeyRef.current = null
      suppressUrlDrillDownRestoreRef.current = false
      desiredUrlDrillDownKeyRef.current = null
      if (unifiedMetricDrillDown) {
        setUnifiedMetricDrillDown(null)
      }
      return
    }

    if (!hasUrlChanged && currentUrlDrillDownKey !== desiredUrlDrillDownKey) {
      return
    }

    desiredUrlDrillDownKeyRef.current = currentUrlDrillDownKey

    if (!currentUrlDrillDownKey) {
      if (unifiedMetricDrillDown) {
        setUnifiedMetricDrillDown(null)
      }
      return
    }

    if (currentStateDrillDownKey === currentUrlDrillDownKey) {
      return
    }

    const nextDrillDownState = getCachedDrillDownState(currentUrlDrillDownKey)

    if (nextDrillDownState) {
      setUnifiedMetricDrillDown(nextDrillDownState)
    }
  }, [
    currentStateDrillDownKey,
    currentUrlDrillDownKey,
    getCachedDrillDownState,
    queryParams,
    unifiedMetricDrillDown,
  ])

  const parentUnifiedMetricDrillDown = useMemo(() => {
    if (!unifiedMetricDrillDown?.parentStateId) {
      return null
    }

    return getCachedDrillDownState(
      getDrillDownStateKey(unifiedMetricDrillDown.parentStateId),
    )
  }, [getCachedDrillDownState, unifiedMetricDrillDown])

  const openParentUnifiedMetricDrillDown = useCallback(() => {
    if (!parentUnifiedMetricDrillDown) {
      return
    }

    openDrillDownState(parentUnifiedMetricDrillDown, { history: "replace" })
  }, [openDrillDownState, parentUnifiedMetricDrillDown])

  return {
    unifiedMetricDrillDown,
    parentUnifiedMetricDrillDown,
    openUnifiedMetricDrillDown,
    openParentUnifiedMetricDrillDown,
    closeUnifiedMetricDrillDown,
  }
}
